import React from "react";
import styled from "styled-components";
import Divider from "@/components/common/Divider";
import { palette } from "@/styles/palette";
import { fontFamily } from "@/styles/font";

// 색상 변수 정의 - palette 사용
const COLORS = {
    primary: "#007F73", // 프로젝트 기본 색상 유지
    secondary: "#FFC700", // 프로젝트 보조 색상 유지
    text: {
        primary: palette.black,
        secondary: "#464A4D",
        tertiary: "#767676",
        light: "#A4ADB2"
    },
    background: {
        main: palette.white,
        secondary: "#F9F9F9"
    },
    border: "#E8EEF2"
};

// API 응답 타입 정의
interface AlcoholData {
    altKey: string;
    name: string;
    type: string;
    abv: number;
    capacity: string | null;
    expirationPeriod: string | null;
    ingredients: string | null;
    breweryInfo: {
        breweryAltKey: string;
        breweryName: string;
        productionRegion: string;
    };
    price: string | null;
    awards: string[];
    salesUrlList: string[];
    thumbnailImageUrl: string | null;
    productFeatures: string | null;
    food: string | null;
    isBreweryProvidedAlcohol: boolean;
}

interface ProductDetailProps {
    alcoholData: AlcoholData;
}

interface AwardItemProps {
    text: string;
}

const AwardItemComponent: React.FC<AwardItemProps> = ({ text }) => (
    <AwardItem>
        <IconImage src="/icon/award.png" alt="수상 아이콘" />
        {text}
    </AwardItem>
);

// 아이콘 없이 라벨만 표시하는 컴포넌트 추가
interface LabelOnlyDetailItemProps {
    label: string;
    value: string;
}

const LabelOnlyDetailItemComponent: React.FC<LabelOnlyDetailItemProps> = ({ label, value }) => (
    <DetailItem>
        <DetailLabel>{label}</DetailLabel>
        <DetailValue>{value}</DetailValue>
    </DetailItem>
);

const ProductDetail: React.FC<ProductDetailProps> = ({ alcoholData }) => {
    // 원재료 문자열을 배열로 변환
    const ingredients = alcoholData.ingredients ? alcoholData.ingredients.split(", ") : [];

    // 상세 정보 데이터 구성
    const detailItems = [
        { label: "생산지역", value: alcoholData.breweryInfo?.productionRegion || "정보 없음" },
        { label: "양조장", value: alcoholData.breweryInfo?.breweryName || "정보 없음" },
        { label: "도수", value: alcoholData.abv ? `${alcoholData.abv}%` : "정보 없음" },
        { label: "용량", value: alcoholData.capacity || "정보 없음" },
        { label: "가격", value: alcoholData.price || "정보 없음" },
        { label: "소비기한", value: alcoholData.expirationPeriod || "정보 없음" }
    ];

    // 수상 정보 데이터
    const awards = alcoholData.awards || [];

    // 가격 비교 데이터
    const prices = alcoholData.salesUrlList || [];

    // 판매처 데이터 (salesUrlList와 동일)
    const salesUrls = alcoholData.salesUrlList || [];

    // 외부 링크 열기 함수
    const openExternalLink = (url: string) => {
        if (url) {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

    const handleReportClick = () => {
        openExternalLink("https://tally.so/r/mDapOq");
    };

    return (
        <Container>
            {alcoholData.productFeatures && (
                <>
                    <Section>
                        <SectionTitle>
                            특징
                            {alcoholData.isBreweryProvidedAlcohol && <Badge>양조장 인증 정보</Badge>}
                        </SectionTitle>
                        <Description>{alcoholData.productFeatures}</Description>
                    </Section>
                    <Divider />
                </>
            )}

            {alcoholData.food && (
                <>
                    <Section>
                        <SectionTitle>어울리는 음식</SectionTitle>
                        <Description>{alcoholData.food}</Description>
                    </Section>
                    <Divider />
                </>
            )}

            <Section>
                <SectionTitle>상세 정보</SectionTitle>
                <DetailContainer>
                    <DetailList>
                        {detailItems.map((item, index) => (
                            <LabelOnlyDetailItemComponent key={index} label={item.label} value={item.value} />
                        ))}
                    </DetailList>
                    <ReportButton onClick={handleReportClick}>
                        <img src="/icon/email.png" alt="제보하기" />
                        <span>잘못된 정보 제보하기</span>
                    </ReportButton>
                </DetailContainer>
            </Section>

            {ingredients.length > 0 && (
                <>
                    <Divider />
                    <Section>
                        <SectionTitle>원재료명</SectionTitle>
                        <SourceInfo>ⓘ 식약처 제공 정보 기준으로 표시됩니다.</SourceInfo>
                        <IngredientList>
                            {ingredients.map((ingredient, index) => (
                                <IngredientItem key={index}>{ingredient}</IngredientItem>
                            ))}
                        </IngredientList>
                    </Section>
                </>
            )}

            {awards.length > 0 && (
                <>
                    <Divider />
                    <Section>
                        <SectionTitle>수상정보</SectionTitle>
                        <AwardList>
                            {awards.map((award, index) => (
                                <AwardItemComponent key={index} text={award} />
                            ))}
                        </AwardList>
                    </Section>
                </>
            )}

            {salesUrls.length > 0 && (
                <>
                    <Divider />
                    <Section>
                        <SectionTitle>판매처</SectionTitle>
                        <SalesUrlList>
                            {salesUrls.map((salesUrl, index) => (
                                <SalesUrlItem key={index} onClick={() => openExternalLink(salesUrl)}>
                                    <SalesUrlName>공식 홈페이지</SalesUrlName>
                                    <SalesUrlButton>바로가기</SalesUrlButton>
                                </SalesUrlItem>
                            ))}
                        </SalesUrlList>
                    </Section>
                </>
            )}
        </Container>
    );
};

export default ProductDetail;

const commonTextStyles = `
    font-family: ${fontFamily.base};
    font-style: normal;
    color: ${COLORS.text.primary};
`;

const Container = styled.article`
    ${commonTextStyles}
    width: 100%;
    height: 100%;
    font-size: clamp(14px, 2.8vw, 18px);
    line-height: 1.5;
    font-weight: 400;
`;

const Description = styled.p`
    ${commonTextStyles}
    font-weight: 400;
    font-size: 16px;
    line-height: 1.5;
    color: ${COLORS.text.secondary};
    margin: 12px 0;

    @media screen and (max-width: 480px) {
        font-size: 14px;
    }
`;

const Section = styled.section`
    margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
    ${commonTextStyles}
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: clamp(18px, 3.5vw, 24px);
    line-height: 1.3;
    font-weight: 600;
    margin-bottom: 16px;

    @media screen and (max-width: 480px) {
        margin-bottom: 12px;
    }
`;

const SubText = styled.span`
    ${commonTextStyles}
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    line-height: 1.5;
    color: ${COLORS.text.tertiary};
    margin-left: 4px;

    @media screen and (max-width: 480px) {
        font-size: 12px;
    }
`;

const AwardList = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 16px;
    list-style: none;
    padding: 0;
    margin: 0;
`;

const AwardItem = styled.li`
    ${commonTextStyles}
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 400;
    font-size: 16px;
    line-height: 1.5;

    @media screen and (max-width: 480px) {
        font-size: 14px;
    }
`;

const DetailList = styled.dl`
    display: flex;
    flex-direction: column;
    gap: 3px;
    margin-left: 4px;
    width: 100%;
`;

const DetailItem = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    width: 100%;
    padding: 8px 0;
    border-bottom: 1px solid ${COLORS.border};

    &:last-child {
        border-bottom: none;
    }
`;

const DetailLabel = styled.dt`
    ${commonTextStyles}
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 100px;
    width: 100px;
    font-weight: 600;
    font-size: 16px;
    line-height: 1.5;
    color: ${COLORS.text.secondary};

    @media screen and (max-width: 480px) {
        min-width: 90px;
        width: 90px;
        font-size: 15px;
    }
`;

const DetailValue = styled.dd`
    ${commonTextStyles}
    font-weight: 400;
    font-size: 16px;
    line-height: 1.5;
    color: ${COLORS.text.secondary};
    margin: 0;
    flex: 1;
    word-break: keep-all;
    overflow-wrap: break-word;

    @media screen and (max-width: 480px) {
        font-size: 15px;
    }
`;

const IngredientList = styled.ul`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
    padding: 0;
    list-style: none;
    width: 100%;
`;

const IngredientItem = styled.li`
    ${commonTextStyles}
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 4px 12px;
    height: 28px;
    border: 1px solid ${COLORS.primary};
    border-radius: 12px;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.5;
    color: ${COLORS.primary};

    @media screen and (max-width: 480px) {
        font-size: 12px;
    }
`;

const PriceList = styled.div`
    display: flex;
    gap: 24px;
    margin-top: 16px;

    @media screen and (max-width: 480px) {
        flex-direction: column;
        gap: 16px;
    }
`;

const PriceColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media screen and (max-width: 480px) {
        width: 100%;
    }
`;

const PriceItem = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;

    @media screen and (max-width: 480px) {
        justify-content: space-between;
    }
`;

const PriceName = styled.div`
    ${commonTextStyles}
    width: 120px;
    font-weight: 500;
    font-size: 16px;
    line-height: 1.5;

    @media screen and (max-width: 480px) {
        font-size: 14px;
    }
`;

const PriceValue = styled.div`
    ${commonTextStyles}
    font-weight: 400;
    font-size: 16px;
    line-height: 1.5;
    color: ${COLORS.text.secondary};

    @media screen and (max-width: 480px) {
        font-size: 14px;
    }
`;

const IconImage = styled.img`
    width: 16px;
    height: 16px;
`;

const StyledIconLabel = styled.div`
    ${commonTextStyles}
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Badge = styled.div`
    ${commonTextStyles}
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 2px 8px;
    background: ${COLORS.secondary};
    border: none;
    border-radius: 12px;
    font-weight: 600;
    font-size: 10px;
    line-height: 1.4;
    color: ${COLORS.text.primary};
    box-shadow: 0 1px 2px rgba(255, 199, 0, 0.2);
    transition: all 0.2s ease-in-out;

    &:hover {
        background: #ffd02e;
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(255, 199, 0, 0.3);
    }

    &:active {
        transform: translateY(0);
        background: #f5be00;
        box-shadow: 0 2px 4px rgba(255, 199, 0, 0.2);
    }

    svg {
        width: 14px;
        height: 14px;
        fill: ${COLORS.text.primary};
    }
`;

const SalesUrlList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;
    width: 100%;
`;

const SalesUrlItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background-color: ${COLORS.background.secondary};
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
        background-color: #f0f0f0;
    }

    @media screen and (max-width: 480px) {
        padding: 10px 12px;
    }
`;

const SalesUrlName = styled.div`
    ${commonTextStyles}
    font-weight: 500;
    font-size: 16px;
    line-height: 1.5;
    color: ${COLORS.text.primary};

    @media screen and (max-width: 480px) {
        font-size: 14px;
    }
`;

const SalesUrlButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 6px 12px;
    background-color: ${COLORS.primary};
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
        background-color: #006a61;
    }

    @media screen and (max-width: 480px) {
        font-size: 12px;
        padding: 4px 10px;
    }
`;

const DetailContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    width: 100%;
`;

const ReportButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 10px;
    background: #f5f5f5;
    border: 1px solid #e5e5e5;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 12px;

    &:hover {
        background: #eeeeee;
    }

    img {
        width: 12px;
        height: 12px;
    }

    span {
        font-size: 12px;
        color: #767676;
        font-weight: 500;
    }

    @media screen and (max-width: 480px) {
        padding: 5px 8px;
        margin-top: 10px;

        span {
            font-size: 11px;
        }
    }
`;

const TitleWithInfo = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 16px;

    @media screen and (max-width: 480px) {
        margin-bottom: 12px;
    }
`;

const InfoTooltip = styled.span`
    position: relative;
    display: inline-flex;
    align-items: center;
    margin-left: 6px;
    cursor: help;

    &:hover > span:last-child {
        visibility: visible;
        opacity: 1;
    }
`;

const InfoIcon = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    font-size: 12px;
    color: ${palette.gray[600]};
    border-radius: 50%;
    border: 1px solid ${palette.gray[300]};
`;

const TooltipText = styled.span`
    visibility: hidden;
    opacity: 0;
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background-color: ${palette.gray[800]};
    color: white;
    text-align: center;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: normal;
    white-space: nowrap;
    transition: opacity 0.3s;
    z-index: 1;

    &::after {
        content: "";
        position: absolute;
        top: 100%;
        left: 50%;
        margin-left: -4px;
        border-width: 4px;
        border-style: solid;
        border-color: ${palette.gray[800]} transparent transparent transparent;
    }
`;

const SourceInfo = styled.p`
    font-size: 12px;
    color: ${palette.gray[500]};
    margin: -12px 0 16px 0;
`;
